import { Link } from "react-router-dom";
import { ArrowLeft, User, Bell, CreditCard, Users, Link2, Save, Shield } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

function DemoResetButton() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')

  const handleReset = async () => {
    setStatus('loading')
    try {
      const res = await fetch('/api/engine/demo/reset', { method: 'POST' })
      if (!res.ok) throw new Error('Reset failed')
      setStatus('done')
      setTimeout(() => setStatus('idle'), 3000)
    } catch {
      setStatus('error')
      setTimeout(() => setStatus('idle'), 3000)
    }
  }

  return (
    <button
      onClick={handleReset}
      disabled={status === 'loading'}
      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 ${
        status === 'done'
          ? 'bg-green-500/10 border border-green-500/30 text-green-400'
          : status === 'error'
            ? 'bg-red-500/10 border border-red-500/30 text-red-400'
            : 'bg-[#02182B] border border-[#2a4a5c] text-white hover:border-[#FD802E]/40'
      }`}
    >
      {status === 'loading' ? '⏳ Resetting…' :
       status === 'done'    ? '✓ Reset complete' :
       status === 'error'   ? '✗ Reset failed' :
       '↺ Reset Demo Project'}
    </button>
  )
}

export function Settings() {
  const [activeTab, setActiveTab] = useState<"account" | "team" | "billing" | "integrations" | "notifications">("account");

  const tabs = [
    { id: "account" as const, label: "Account", icon: User },
    { id: "team" as const, label: "Team", icon: Users },
    { id: "billing" as const, label: "Billing", icon: CreditCard },
    { id: "integrations" as const, label: "Integrations", icon: Link2 },
    { id: "notifications" as const, label: "Notifications", icon: Bell },
  ];

  return (
    <div className="min-h-screen bg-[#02182B]">
      <header className="border-b border-[#233D4C] bg-[#02182B]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-white text-xl font-semibold">Settings</h1>
              <p className="text-gray-400 text-sm">Manage your account and preferences</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-6">
          <div className="w-64 flex-shrink-0">
            <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === tab.id ? "bg-[#FD802E]/10 text-[#FD802E]" : "text-gray-400 hover:text-white hover:bg-[#02182B]"}`}
                >
                  <tab.icon size={20} />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1">
            {activeTab === "account" && <AccountSettings />}
            {activeTab === "team" && <TeamSettings />}
            {activeTab === "billing" && <BillingSettings />}
            {activeTab === "integrations" && <IntegrationsSettings />}
            {activeTab === "notifications" && <NotificationSettings />}
          </div>
        </div>
      </main>
    </div>
  );
}

function AccountSettings() {
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState(user?.user_metadata?.display_name ?? "");
  const [jobTitle, setJobTitle] = useState(user?.user_metadata?.job_title ?? "");
  const [company, setCompany] = useState(user?.user_metadata?.company ?? "");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await supabase.auth.updateUser({ data: { display_name: displayName, job_title: jobTitle, company } });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return;
    await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResetSent(true);
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
        <h2 className="text-white text-lg font-semibold mb-6">Profile Information</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your display name"
              className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] focus:ring-2 focus:ring-[#FD802E]/20 transition-colors"
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Job Title</label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Your job title"
              className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] focus:ring-2 focus:ring-[#FD802E]/20 transition-colors"
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Company</label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Your company"
              className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] focus:ring-2 focus:ring-[#FD802E]/20 transition-colors"
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={user?.email ?? ""}
              readOnly
              className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-3 text-gray-400 cursor-not-allowed focus:outline-none transition-colors"
            />
            <p className="text-gray-500 text-xs mt-1">Email cannot be changed in this version.</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 mt-6">
          {saved && <span className="text-green-400 text-sm">✓ Saved successfully</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-[#FD802E] text-[#233D4C] rounded-lg hover:bg-[#ff8f45] transition-colors font-semibold disabled:opacity-50"
          >
            <Save size={18} />{saving ? "Saving…" : saved ? "Saved!" : "Save Changes"}
          </button>
        </div>
      </div>
      <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
        <h2 className="text-white text-lg font-semibold mb-4">Security</h2>
        {resetSent ? (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-3 text-green-400 text-sm">
            ✓ Password reset email sent to {user?.email}
          </div>
        ) : (
          <button
            onClick={handlePasswordReset}
            className="flex items-center gap-2 px-6 py-3 bg-[#02182B] text-white rounded-lg hover:bg-[#0a2438] transition-colors font-medium"
          >
            <Shield size={18} />Change Password
          </button>
        )}
      </div>
      {/* Demo reset */}
      <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
        <h2 className="text-white text-lg font-semibold mb-2">Demo</h2>
        <p className="text-gray-400 text-sm mb-4">Reset the demo project to a clean, presentation-ready state with seeded data.</p>
        <DemoResetButton />
      </div>
    </div>
  );
}

function TeamSettings() {
  const teamMembers = [
    { name: "John Smith", email: "john.smith@company.com", role: "Admin", status: "Active" },
    { name: "Sarah Johnson", email: "sarah.j@company.com", role: "Editor", status: "Active" },
    { name: "Mike Davis", email: "mike.d@company.com", role: "Viewer", status: "Pending" },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-lg font-semibold">Team Members</h2>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#FD802E] text-[#233D4C] rounded-lg hover:bg-[#ff8f45] transition-colors font-semibold text-sm">
            <User size={16} />Invite Member
          </button>
        </div>
        <div className="space-y-3">
          {teamMembers.map((member, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-[#02182B] rounded-lg border border-[#2a4a5c]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FD802E]/10 flex items-center justify-center text-[#FD802E] font-semibold">
                  {member.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <div>
                  <div className="text-white font-medium">{member.name}</div>
                  <div className="text-gray-400 text-sm">{member.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-gray-400 text-sm">{member.role}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${member.status === "Active" ? "bg-green-500/10 text-green-400" : "bg-yellow-500/10 text-yellow-400"}`}>
                  {member.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BillingSettings() {
  return (
    <div className="space-y-6">
      <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
        <h2 className="text-white text-lg font-semibold mb-6">Current Plan</h2>
        <div className="bg-[#FD802E]/10 border border-[#FD802E]/30 rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-white text-xl font-bold mb-1">Professional</h3>
              <p className="text-gray-300 text-sm">Unlimited projects and team members</p>
            </div>
            <div className="text-right">
              <div className="text-white text-2xl font-bold">£299</div>
              <div className="text-gray-400 text-sm">per month</div>
            </div>
          </div>
          <div className="space-y-2 mb-6">
            {["Unlimited AI conversations", "Advanced analytics & insights", "Priority support"].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-[#FD802E]" />{f}
              </div>
            ))}
          </div>
          <button className="w-full px-6 py-3 bg-[#FD802E] text-[#233D4C] rounded-lg hover:bg-[#ff8f45] transition-colors font-semibold">
            Upgrade to Enterprise
          </button>
        </div>
        <div className="text-sm text-gray-400">
          <div className="flex justify-between py-2"><span>Next billing date:</span><span className="text-white font-medium">April 24, 2026</span></div>
          <div className="flex justify-between py-2"><span>Payment method:</span><span className="text-white font-medium">•••• 4242</span></div>
        </div>
      </div>
      <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
        <h2 className="text-white text-lg font-semibold mb-4">Billing History</h2>
        <div className="space-y-2">
          {[{ date: "Mar 24, 2026", amount: "£299.00" }, { date: "Feb 24, 2026", amount: "£299.00" }, { date: "Jan 24, 2026", amount: "£299.00" }].map((invoice, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-[#02182B] rounded border border-[#2a4a5c]">
              <span className="text-gray-300 text-sm">{invoice.date}</span>
              <span className="text-white font-medium">{invoice.amount}</span>
              <span className="text-green-400 text-sm">Paid</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function IntegrationsSettings() {
  const integrations = [
    { name: "Greenhouse ATS", description: "Sync candidates to your ATS", connected: false, icon: "🌱" },
    { name: "Lever", description: "Push candidates to Lever", connected: false, icon: "🔌" },
    { name: "LinkedIn Recruiter", description: "Import candidate profiles", connected: true, icon: "💼" },
    { name: "Google Workspace", description: "Calendar and email integration", connected: true, icon: "📧" },
    { name: "Slack", description: "Team notifications", connected: false, icon: "💬" },
    { name: "Zapier", description: "Connect 5000+ apps", connected: false, icon: "🤖" },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
        <h2 className="text-white text-lg font-semibold mb-6">Available Integrations</h2>
        <div className="grid gap-4">
          {integrations.map((integration, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-[#02182B] rounded-lg border border-[#2a4a5c]">
              <div className="flex items-center gap-4">
                <div className="text-3xl">{integration.icon}</div>
                <div>
                  <div className="text-white font-medium">{integration.name}</div>
                  <div className="text-gray-400 text-sm">{integration.description}</div>
                </div>
              </div>
              {integration.connected ? (
                <button className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors font-medium text-sm">Disconnect</button>
              ) : (
                <button className="px-4 py-2 bg-[#FD802E] text-[#233D4C] rounded-lg hover:bg-[#ff8f45] transition-colors font-semibold text-sm">Connect</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NotificationSettings() {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const [prefs, setPrefs] = useState(() => {
    const stored = user?.user_metadata?.notification_prefs;
    return {
      newMatches: stored?.newMatches ?? true,
      prescreenComplete: stored?.prescreenComplete ?? true,
      outreachResponses: stored?.outreachResponses ?? true,
      teamUpdates: stored?.teamUpdates ?? false,
      weeklyDigest: stored?.weeklyDigest ?? false,
    };
  });

  const SETTINGS = [
    { key: 'newMatches' as const,        label: "New candidate matches",  description: "Get notified when new candidates are identified" },
    { key: 'prescreenComplete' as const, label: "Pre-screen completions", description: "AI pre-screen call results are ready" },
    { key: 'outreachResponses' as const, label: "Outreach responses",     description: "Candidates reply to your outreach" },
    { key: 'teamUpdates' as const,       label: "Team updates",           description: "Changes made by team members" },
    { key: 'weeklyDigest' as const,      label: "Weekly digest",          description: "Summary of all activity" },
  ];

  const handleSave = async () => {
    setSaving(true);
    await supabase.auth.updateUser({ data: { notification_prefs: prefs } });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
        <h2 className="text-white text-lg font-semibold mb-6">Email Notifications</h2>
        <div className="space-y-4">
          {SETTINGS.map((setting) => (
            <label key={setting.key} className="flex items-start justify-between cursor-pointer p-4 bg-[#02182B] rounded-lg border border-[#2a4a5c] hover:border-[#FD802E]/30 transition-colors">
              <div className="flex-1">
                <div className="text-white font-medium mb-1">{setting.label}</div>
                <div className="text-gray-400 text-sm">{setting.description}</div>
              </div>
              <input
                type="checkbox"
                checked={prefs[setting.key]}
                onChange={(e) => setPrefs(p => ({ ...p, [setting.key]: e.target.checked }))}
                className="w-5 h-5 mt-1 rounded border-gray-600 bg-[#233D4C] text-[#FD802E] focus:ring-[#FD802E] focus:ring-offset-0"
              />
            </label>
          ))}
        </div>
        <div className="flex items-center justify-end gap-3 mt-6">
          {saved && <span className="text-green-400 text-sm">✓ Preferences saved</span>}
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-6 py-3 bg-[#FD802E] text-[#233D4C] rounded-lg hover:bg-[#ff8f45] transition-colors font-semibold disabled:opacity-50">
            <Save size={18} />{saving ? "Saving…" : saved ? "Saved!" : "Save Preferences"}
          </button>
        </div>
      </div>
    </div>
  );
}
